import { React, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EditDeleteTable from '../Components/EditDeleteTable';
import AddEditHealthcareWorkerModal from '../Components/AddEditHealthcareWorkerModal';
import DeleteModal from '../Components/DeleteModal';
import { Snackbar, Alert, Button } from '@mui/material';

import '../Styles/ViewOfHCWs.css';
import Sidebar from '../Components/Sidebar'; // Import Sidebar
import ChatComponent from './ChatComponent'; // Import Chat Component
import { FaBars } from 'react-icons/fa'; // Import the icon for closing

function ViewOfHCWs() {
    // the columns for the table
    // headerName is what shows up on the website
    // width is the default width of the column, user can adjust it
    const [rows, setRows] = useState([]);
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Set to true to show sidebar initially
    const [selectedHCW, setSelectedHCW] = useState(null); // State for selected HCW
    const url = process.env.REACT_APP_API_URL;

    // Async function to fetch healthcare workers data
    const fetchHealthcareWorkers = async () => {
        try {
            const response = await fetch(
                `${url}/healthcare-worker/list`
            );
            const data = await response.json(); // Convert response to JSON
            setRows(data); // Update rows state with fetched data
        } catch (error) {
            console.error('Error fetching healthcare workers:', error);
            setErrorMessage('Failed to fetch healthcare workers');
            setOpenSnackbar(true); // Show Snackbar when error occurs
        }
    };

    // useEffect to fetch data when the component mounts
    useEffect(() => {
        fetchHealthcareWorkers(); // Call the async function
    }, []); // Empty array means this effect runs once when the component mounts

    const handleSelectHCW = (hcw) => {
        setSelectedHCW(hcw); // Set the selected healthcare worker
        setIsSidebarOpen(false); // Close sidebar after selecting
        navigate('/chat', { state: { selectedHCW: hcw } }); // Navigate to ChatComponent, using the new route
    };

    const handleOpenSidebar = () => {
        setIsSidebarOpen(true); // Open the sidebar
    };

    const columns = [
        // TODO: add column with button
        // cam's code is: onClick={() => handleSelectHCW(row)}
        // { field: 'id', headerName: 'Id'},
        { field: 'first_name', headerName: 'First name' },
        { field: 'last_name', headerName: 'Last name' },
        { field: 'email', headerName: 'Email' },
        { field: 'profession', headerName: 'Profession' },
        { field: 'clinic_name', headerName: 'Clinic name' },
        { field: 'department', headerName: 'Department' },
        // { field: 'latitude_at_creation', headerName: 'Latitude_at_creation'},
        // { field: 'longitude_at_creation', headerName: 'Longitude_at_creation'},

        // TODO: is there a better way to do this?
        // TODO: verify this works with the coordinates values/variable types we will be getting from the backend
        // give a google maps link to the location
        {
            field: 'location',
            headerName: 'Initial Location',
            width: 220,
            valueGetter: (value, row) => {
                // https://www.google.com/maps?q=latitude,longitude
                <a
                    href={`https://www.google.com/maps?q=${row.latitude_at_creation},${row.longitude_at_creation}`}
                >
                    Location in Maps
                </a>;
            },
        },
        { field: 'country_name', headerName: 'Country' },

        {
            field: 'view_ai_conversations',
            headerName: 'View AI Conversations',
            renderCell: (params) => (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleViewConversations(params.row.id)}
                >
                    View AI Conversations
                </Button>
            ),
        },
    ];

    const handleViewConversations = (healthcare_worker_id) => {
        if (!healthcare_worker_id) {
            console.error('Healthcare worker ID is undefined');
            return;
        }
        // TODO: fix this url
        navigate(`/viewofhcws/${healthcare_worker_id}/ai_conversations`);
    };

    // TODO: should everyone be able to delete a HCW??
    // all users can edit healthcare workers
    const canEdit = () => {
        // const userType = localStorage.getItem('userType');
        return true;
    };

    // TODO: should everyone be able to delete a HCW??
    const canDelete = () => {
        // const role = localStorage.getItem('role');
        // return role === '' || role === '';
        return true;
    };

    const deleteHealthcareWorker = async (id) => {
        try {
            console.log('row', id);
            const response = await fetch(
                `${url}/healthcare-worker/${id}`,
                {
                    method: 'DELETE',
                }
            );
            if (!response.ok) {
                throw new Error('Failed to delete healthcare worker');
            }
            fetchHealthcareWorkers();
        } catch (error) {
            console.error('Error deleting healthcare worker:', error);
        }
    };

    const addEditHealthcareWorker = async (data, id) => {
        if (id) {
            editHealthcareWorker(data, id);
        } else {
            addHealthcareWorker(data);
        }
    };

    const editHealthcareWorker = async (data, id) => {
        try {
            console.log('row in editHealthcareWorker', id, data);
            const response = await fetch(
                `${url}/healthcare-worker/${id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(data),
                }
            );
            if (!response.ok) {
                throw new Error('Failed to update healthcare worker');
            }
            fetchHealthcareWorkers();
        } catch (error) {
            console.error('Error updating healthcare worker:', error);
        }
    };

    const addHealthcareWorker = async (data) => {
        try {
            // Set default values for long/lat until we figure that out
            data.latitude_at_creation = data.latitude_at_creation || 0.0;
            data.longitude_at_creation = data.longitude_at_creation || 0.0;

            console.log('row in addHealthcareWorker', data);
            const response = await fetch(
                `${url}/healthcare-worker`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(data),
                }
            );
            if (!response.ok) {
                throw new Error('Failed to add healthcare worker');
            }
            fetchHealthcareWorkers();
        } catch (error) {
            console.error('Error adding healthcare worker:', error);
        }
    };

    // Handle closing of the Snackbar
    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    // the message format that should get used in the delete confirmation modal (popup) for this table
    // need this since we want a different format on other tables that use this same base component
    const healthcareWorkerConfirmMessage = (row) =>
        `${row?.first_name || 'Unknown First Name'} ${
            row?.last_name || 'Unknown Last Name'
        }, in ${row?.country_name || 'Unknown Country'}`;

    const openAddHealthcareWorkerModal = useRef(null);

    return (
        <div className="view-of-hcws-container">
            <Sidebar
                isOpen={isSidebarOpen}
                toggleSidebar={() => setIsSidebarOpen(false)} // Close sidebar
                rows={rows}
                onSelectHCW={handleSelectHCW}
            />
            {selectedHCW ? (
                <ChatComponent selectedHCW={selectedHCW} />
            ) : (
                <div>
                    <h2>Healthcare Workers</h2>

                    <Button
                        class="add-button"
                        variant="contained"
                        onClick={() => {
                            if (openAddHealthcareWorkerModal.current) {
                                openAddHealthcareWorkerModal.current(); // Trigger modal to open for adding a healthcare worker
                            }
                        }}
                    >
                        Add Healthcare worker
                    </Button>
                </div>
            )}
            {/* Button to open sidebar when it is closed */}
            {!isSidebarOpen && (
                <button
                    className="open-sidebar-button"
                    onClick={handleOpenSidebar}
                >
                    {/* Change from "Open Sidebar" to an icon at the top left */}
                    <FaBars size={20} />
                </button>
            )}

            <EditDeleteTable
                rows={rows}
                columns={columns}
                editModal={AddEditHealthcareWorkerModal}
                deleteModal={DeleteModal}
                showEditButton={canEdit()}
                showDeleteButton={canDelete()}
                customConfirmMessage={healthcareWorkerConfirmMessage}
                onAdd={(handler) => {
                    openAddHealthcareWorkerModal.current = handler; // Store the open modal handler
                }}
                onEdit={addEditHealthcareWorker}
                onConfirmDelete={deleteHealthcareWorker}
            />
            {/* Snackbar for error messages */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity="error">
                    {errorMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default ViewOfHCWs;
